import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Accordion as MuiAccordion,
  useTheme,
} from '@mui/material'
import type { ReactNode } from 'react'
import React from 'react'
import { BsChevronDoubleUp } from 'react-icons/bs'

import Icon from '@/components/Icon'

interface CommonAccordionProps {
  title: string | ReactNode
  children: ReactNode
  defaultExpanded?: boolean
  expandIcon?: ReactNode
  titleEndContent?: ReactNode
  sx?: any
  accordionDetailsSx?: any
  accordionSummarySx?: any
  titleSx?: any
  controlProps?: {
    expanded: boolean
    onChange?: (event?: React.SyntheticEvent, expanded?: boolean) => void
  }
}

const Accordion: React.FC<CommonAccordionProps> = ({
  title,
  children,
  defaultExpanded = true,
  expandIcon = <Icon icon={BsChevronDoubleUp} />,
  titleEndContent,
  sx,
  accordionDetailsSx,
  accordionSummarySx,
  titleSx,
  controlProps,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = React.useState(defaultExpanded)

  const handleExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setExpanded(!expanded)
    controlProps?.onChange?.(event, !expanded)
  }

  return (
    <MuiAccordion
      {...(controlProps ? { ...controlProps } : { expanded })}
      sx={{
        background: 'inherit',
        color: theme.palette.text.primary,
        '&.Mui-expanded': {
          marginTop: 0,
          marginBottom: 0,
        },
        ...sx,
      }}
    >
      <AccordionSummary
        expandIcon={
          <Box sx={{ cursor: 'pointer' }} onClick={handleExpandClick}>
            {expandIcon}
          </Box>
        }
        aria-controls="common-accordion-content"
        id="common-accordion-header"
        sx={{
          p: '2px 0',
          overflow: 'hidden',
          cursor: 'default !important',
          minHeight: 'auto !important',
          '& .MuiAccordionSummary-content': {
            margin: '0.25rem 0 !important',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            margin: '0.25rem !important',
          },
          ...accordionSummarySx,
        }}
      >
        <Box display="flex" alignItems="center" width="100%">
          <Box fontSize="16px" color={theme.palette.text.primary} {...titleSx}>
            {title}
          </Box>
          {titleEndContent && (
            <Box ml="auto" display="flex" alignItems="center">
              {titleEndContent}
            </Box>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          padding: '0.5rem 0.5rem 1rem 0.5rem',
          ...accordionDetailsSx,
        }}
      >
        {children}
      </AccordionDetails>
    </MuiAccordion>
  )
}

export default Accordion
